export type SidebarClickedOn =
  | 'inicio'
  | 'mis_ofertas'
  | 'agenda'
  | 'facturacion'
  | 'configurar_reservas'
  | 'experiencias'
  | 'mis_experiencias'
  | 'mi_contenido'
  | 'resenas'
  | 'metricas'
  | 'beneficios'
  | 'gestionar_usuarios'
  | 'centro_ayuda';

export type CtaSection =
  | 'novedades'
  | 'agenda_de_hoy'
  | 'tus_resenas'
  | 'resenas';

export type ItemId = string | number;

export interface EventParams {
  sidebar_click: {
    clicked_on: SidebarClickedOn;
    item_index: number;
    link_url: string;
  };
  header_click: {
    clicked_on:
      | 'hacer_reserva'
      | 'sumar_experiencia'
      | 'mi_perfil'
      | 'notificaciones';
    link_url: string;
  };
  cta_click: {
    section_title: CtaSection;
    clicked_on:
      | 'banner'
      | 'ir_a_agenda'
      | 'calificacion_promedio'
      | 'responder'
      | 'enviar_respuesta';
    link_url: string;
  };
  mis_experiencias: {
    clicked_on:
      | 'exp_activas'
      | 'exp_inactivas'
      | 'exp_pendientes'
      | 'exp_rechazadas';
  };
  exp_activas: { clicked_on: 'solicitar_cambios'; item_id: ItemId };
  exp_inactivas: {
    clicked_on:
      | 'eliminar_actividad'
      | 'eliminar_definitivamente'
      | 'activar_exp';
    item_id: ItemId;
  };
  exp_rechazadas: { clicked_on: 'modificar_nuevamente'; item_id: ItemId };
  gestionar_usuarios: { clicked_on: 'invitar_usuario' | 'enviar_invitacion' };
}

export type AnalyticsEvent = keyof EventParams;

export type AnalyticsParams = Record<string, string | number | null>;

type ContextKey<E extends AnalyticsEvent> = Exclude<
  keyof EventParams[E],
  'clicked_on'
>;

/** Parámetros de cada evento que aporta el contexto y no la constante. */
export const CONTEXT_PARAMS: {
  readonly [E in AnalyticsEvent]: readonly ContextKey<E>[];
} = {
  sidebar_click: ['item_index', 'link_url'],
  header_click: ['link_url'],
  cta_click: ['section_title', 'link_url'],
  mis_experiencias: [],
  exp_activas: ['item_id'],
  exp_inactivas: ['item_id'],
  exp_rechazadas: ['item_id'],
  gestionar_usuarios: [],
};

/** Valor de `v-track-scope`: contexto que heredan los `v-track` de adentro. */
export interface TrackScopeParams {
  section_title?: CtaSection;
  item_id?: ItemId;
  item_index?: number;
  link_url?: string;
}

export type TrackEvent = {
  [E in AnalyticsEvent]: {
    event: E;
    params: Pick<EventParams[E], 'clicked_on'> & Partial<EventParams[E]>;
  };
}[AnalyticsEvent];

function on<E extends AnalyticsEvent>(
  event: E,
  clickedOn: EventParams[E]['clicked_on'],
): TrackEvent {
  return { event, params: { clicked_on: clickedOn } } as TrackEvent;
}

export const SIDEBAR_EVENTS = {
  home: on('sidebar_click', 'inicio'),
  deals: on('sidebar_click', 'mis_ofertas'),
  schedule: on('sidebar_click', 'agenda'),
  liquidations: on('sidebar_click', 'facturacion'),
  reservationConfiguration: on('sidebar_click', 'configurar_reservas'),
  experiences: on('sidebar_click', 'experiencias'),
  myExperiences: on('sidebar_click', 'mis_experiencias'),
  socialMediaCovers: on('sidebar_click', 'mi_contenido'),
  reviews: on('sidebar_click', 'resenas'),
  charts: on('sidebar_click', 'metricas'),
  benefits: on('sidebar_click', 'beneficios'),
  usersManagement: on('sidebar_click', 'gestionar_usuarios'),
  helpCenter: on('sidebar_click', 'centro_ayuda'),
} as const;

export const HEADER_EVENTS = {
  createBooking: on('header_click', 'hacer_reserva'),
  createExperience: on('header_click', 'sumar_experiencia'),
  profile: on('header_click', 'mi_perfil'),
  notifications: on('header_click', 'notificaciones'),
} as const;

/** Home y /reseñas; la sección sale del `v-track-scope`. */
export const CTA_EVENTS = {
  announcement: on('cta_click', 'banner'),
  goToSchedule: on('cta_click', 'ir_a_agenda'),
  averageRating: on('cta_click', 'calificacion_promedio'),
  answer: on('cta_click', 'responder'),
  answerSent: on('cta_click', 'enviar_respuesta'),
} as const;

export const EXPERIENCE_TAB_EVENTS = {
  active: on('mis_experiencias', 'exp_activas'),
  inactive: on('mis_experiencias', 'exp_inactivas'),
  pending: on('mis_experiencias', 'exp_pendientes'),
  rejected: on('mis_experiencias', 'exp_rechazadas'),
} as const;

export const EXPERIENCE_ACTION_EVENTS = {
  requestChanges: on('exp_activas', 'solicitar_cambios'),
  delete: on('exp_inactivas', 'eliminar_actividad'),
  confirmDelete: on('exp_inactivas', 'eliminar_definitivamente'),
  activate: on('exp_inactivas', 'activar_exp'),
  editAgain: on('exp_rechazadas', 'modificar_nuevamente'),
} as const;

export const USERS_MANAGEMENT_EVENTS = {
  invite: on('gestionar_usuarios', 'invitar_usuario'),
  inviteSent: on('gestionar_usuarios', 'enviar_invitacion'),
} as const;
